import React, { useCallback } from "react";
import useGame from "../../hooks/useGame";
import Typography from "@mui/material/Typography";
import GameTimer from "../gameTimer/GameTimer";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Card from "@mui/material/Card";
import SettingsIcon from "@mui/icons-material/Settings";
import IOPSConfigModal from "../game/IOPSConfigModal";
import { useAppSelector } from "../../hooks";
import { selectInitialRootIOPS, selectRootBlockIOPS, selectTrafficPattern } from "../../store/game/selectors";
import { OperationTypes } from "../../models/block";
import TrafficPatternIcon from "../common/TrafficPatternIcon";
import Box from "@mui/material/Box";
import { getCompactNumberStr } from "../../utils/iops-utils";

const ScoreBoard: React.FC = () => {
  const [gameState, startGame, pauseGame, resumeGame] = useGame();
  const isMobile = useMediaQuery(`(max-width: 760px)`);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);

  const initialIOPS = useAppSelector((state) => selectInitialRootIOPS(state.game));
  const currentRootIOPS = useAppSelector((state) => selectRootBlockIOPS(state.game));
  const trafficPattern = useAppSelector((state) => selectTrafficPattern(state.game));

  const iopsToDisplay = gameState.isStarted && currentRootIOPS ? currentRootIOPS : initialIOPS;

  const totalIOPS = iopsToDisplay?.total || 0;
  const readTotal = iopsToDisplay?.byOperationType[OperationTypes.READ]?.total || 0;
  const readPct = totalIOPS > 0 ? Math.round((readTotal / totalIOPS) * 100) : 0;
  const writePct = 100 - readPct;


  const handleStartPauseClick = useCallback(() => {
    if (!gameState.isStarted) {
      startGame();
    } else if (gameState.isRunning) {
      pauseGame();
    } else {
      resumeGame();
    }
  }, [gameState, pauseGame, startGame, resumeGame]);
  return (
    <Card
      style={{
        position: "absolute",
        top: isMobile ? 0 : "20px",
        right: isMobile ? 0 : "20px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-evenly",
        gap: isMobile ? "10px" : "15px",
        zIndex: 100,
        width: isMobile ? "calc(100vw - 20px)" : "auto",
        padding: isMobile ? "10px 10px 10px 0px" : "20px",
        margin: "5px",
        alignItems: "center",
      }}
      elevation={5}
    >
      <GameTimer
        elapsedTime={gameState.timeElapsedInSec}
        isRunning={gameState.isRunning}
        isStarted={gameState.isStarted}
      />

      <Typography
        variant="h2"
        style={{
          color: "#2083E6",
          fontSize: isMobile ? "22px" : "32px",
          fontWeight: 800,
          display: "none", // Hidden for now
        }}
      >
        Lv.{gameState.level}
      </Typography>
      <Typography
        variant="h2"
        style={{
          color: "#077620",
          fontSize: isMobile ? "22px" : "32px",
          fontWeight: 800,
          display: "none", // Hidden for now
        }}
      >
        ${gameState.accountBalance.toFixed(2)}
      </Typography>

      {/* IOPS Display */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
          mr: 1,
          color: "#0d47a1",
          border: "1px solid #0d47a1",
          borderRadius: "4px",
          padding: "5px 10px",
          height: isMobile ? "35px" : "50px",
        }}
      >
        {trafficPattern && <TrafficPatternIcon pattern={trafficPattern} color="inherit" />}
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center"}}>
          <Typography variant="body2" sx={{ fontWeight: "bold", lineHeight: 1, marginBottom: "5px" }}>
            Total TPS: {getCompactNumberStr(totalIOPS)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: "0.7rem", lineHeight: 1 }}>
            Read: {readPct}% Write: {writePct}%
          </Typography>
        </Box>
      </Box>

      <IconButton
        aria-label="settings"
        onClick={() => setIsSettingsModalOpen(true)}
        sx={{
          backgroundColor: "#0d47a1",
          color: "white",
          width: isMobile ? "35px" : "50px",
          height: isMobile ? "35px" : "50px",
          marginTop: isMobile ? "0px" : "0px", // Align with other button
          "&:hover": {
            backgroundColor: "#2083E6",
          },
        }}
      >
        <SettingsIcon />
      </IconButton>
      <IconButton
        aria-label="play pause"
        onClick={handleStartPauseClick}
        sx={{
          backgroundColor: "#0d47a1",
          color: "#0d47a1",
          width: isMobile ? "35px" : "50px",
          height: isMobile ? "35px" : "50px",
          "&:hover": {
            backgroundColor: "#2083E6",
          },
        }}
      >
        {gameState.isRunning && <PauseIcon sx={{ color: "white" }} />}
        {!gameState.isRunning && <PlayArrowIcon sx={{ color: "white" }} />}
      </IconButton>
      <IOPSConfigModal open={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />
    </Card>
  );
};

export default ScoreBoard;
